import { useTranslation } from 'react-i18next';
import { useSession } from '@/entities/session';
import { useDocumentTitle } from '@/shared/lib/use-document-title';
import { useOwnProfile, useUpdateProfile } from '../../entities/profile';
import { EditProfileForm } from '../../features/profile/edit-profile-form';
import { Card, CardContent, CardHeader, CardTitle } from '../../shared/ui/card';
import { ErrorState } from '../../shared/ui/error-state';
import { PageLayout } from '../../shared/ui/page-layout';
import { Skeleton } from '../../shared/ui/skeleton';
import { toast } from '../../shared/ui/sonner';

export function AccountPage() {
  const { t } = useTranslation('account');
  useDocumentTitle(t('title'));
  const { session } = useSession();
  const userId = session?.user.id ?? '';
  const profile = useOwnProfile(userId);
  const update = useUpdateProfile(userId);

  return (
    <PageLayout title={t('title')} description={t('description')} width="narrow">
      <Card>
        <CardHeader>
          <CardTitle asChild className="font-sans">
            <h2>{t('profile.title')}</h2>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {profile.isPending ? (
            <section aria-busy="true" aria-label={t('profile.loading')} className="grid gap-4">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-32 rounded-full" />
            </section>
          ) : null}
          {profile.isError ? (
            <ErrorState
              size="compact"
              title={t('profile.errorTitle')}
              description={profile.error.message}
              onRetry={() => void profile.refetch()}
            />
          ) : null}
          {profile.isSuccess ? (
            <EditProfileForm
              defaultValues={{
                firstName: profile.data?.first_name ?? '',
                lastName: profile.data?.last_name ?? '',
              }}
              email={session?.user.email ?? ''}
              error={update.isError ? t('profile.saveFailed') : null}
              onSubmit={async (values) => {
                try {
                  await update.mutateAsync(values);
                  toast.success(t('profile.saved'));
                } catch {
                  // shown inline through `update.isError`
                }
              }}
            />
          ) : null}
        </CardContent>
      </Card>
    </PageLayout>
  );
}
